import React, { useState, useEffect } from 'react';
import {
  Card,
  Typography,
  Row,
  Col,
  Button,
  Switch,
  Tag,
  List,
  Input,
  message,
  Statistic,
  Form,
  Alert,
} from 'antd';
import {
  EnvironmentOutlined,
  PushpinOutlined,
  CarOutlined,
  ClockCircleOutlined,
  ReloadOutlined,
  SettingOutlined,
  CheckCircleOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation } from 'react-query';
import api from '../services/api';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

const LocationTracking = () => {
  const [isLocationEnabled, setIsLocationEnabled] = useState(true);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [locationLoading, setLocationLoading] = useState(false);

  // Get location permissions
  const { data: permissions, refetch: refetchPermissions } = useQuery(
    'location-permissions',
    async () => {
      const response = await api.get('/provider/location-permissions');
      return response.data;
    },
  );

  // Update location mutation
  const updateLocationMutation = useMutation(
    async (locationData) => {
      const response = await api.post('/provider/location', locationData);
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('Location updated successfully');
      },
      onError: (error) => {
        message.error(
          'Failed to update location: ' + (error.response?.data?.message || error.message),
        );
      },
    },
  );

  // Update permissions mutation
  const updatePermissionsMutation = useMutation(
    async (data) => {
      const response = await api.put('/provider/location-permissions', data);
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('Location settings updated');
        refetchPermissions();
      },
      onError: (error) => {
        message.error(
          'Failed to update settings: ' + (error.response?.data?.message || error.message),
        );
      },
    },
  );

  // Get current location using browser Geolocation API
  const getCurrentGeolocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported by your browser'));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
        },
        (error) => {
          reject(error);
        },
        { enableHighAccuracy: false, timeout: 10000, maximumAge: 60000 },
      );
    });
  };

  // Update location handler
  const handleUpdateLocation = async () => {
    setLocationLoading(true);
    try {
      const location = await getCurrentGeolocation();

      // Reverse geocode to get address
      const address = await reverseGeocode(location.latitude, location.longitude);

      const locationData = {
        latitude: location.latitude,
        longitude: location.longitude,
        address: address,
        is_sharing: true,
      };

      setCurrentLocation(locationData);
      updateLocationMutation.mutate(locationData);
    } catch (error) {
      message.error(
        'Browser could not determine your location. Please enable Location Services in System Settings > Privacy & Security > Location Services and reload this page.',
      );
    } finally {
      setLocationLoading(false);
    }
  };

  // Simple reverse geocode (in production use a proper API)
  const reverseGeocode = async (lat, lng) => {
    try {
      // Try to get address from coordinates using Nominatim (free)
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`,
        {
          headers: {
            'User-Agent': 'KankaliMart/1.0',
          },
        },
      );
      const data = await response.json();
      return data.display_name || `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
    } catch (error) {
      return `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
    }
  };

  // Manual location update
  const handleManualLocation = (values) => {
    const locationData = {
      latitude: parseFloat(values.latitude),
      longitude: parseFloat(values.longitude),
      address: values.address || `${values.latitude}, ${values.longitude}`,
      is_sharing: true,
    };
    setCurrentLocation(locationData);
    updateLocationMutation.mutate(locationData);
  };

  // Toggle location sharing
  const handleToggleSharing = (checked) => {
    setIsLocationEnabled(checked);
    updatePermissionsMutation.mutate({
      location_enabled: checked,
    });
  };

  // Fetch own saved location from backend to show live updates
  const { data: savedLocation } = useQuery(
    'my-location',
    async () => {
      const response = await api.get('/provider/my-location');
      return response.data;
    },
    {
      refetchInterval: 5000,
      enabled: isLocationEnabled,
    },
  );

  useEffect(() => {
    if (savedLocation?.data?.latitude && savedLocation?.data?.longitude) {
      setCurrentLocation(savedLocation.data);
    }
  }, [savedLocation]);

  return (
    <div style={{ padding: '24px' }}>
      <Alert
        message="Background location tracking is active"
        description="Your location is automatically shared every 30 seconds while you're logged in. A green 'Live' indicator in the header shows tracking status."
        type="info"
        showIcon
        icon={<InfoCircleOutlined />}
        style={{ marginBottom: 16 }}
      />

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px',
        }}
      >
        <Title level={2}>
          <EnvironmentOutlined style={{ marginRight: '8px' }} />
          Location Tracking
        </Title>
        <Button icon={<ReloadOutlined />} onClick={handleUpdateLocation} loading={locationLoading}>
          Update Location Now
        </Button>
      </div>

      <Row gutter={[16, 16]}>
        {/* Current Location Card */}
        <Col xs={24} lg={12}>
          <Card title="Current Location">
            {currentLocation ? (
              <div>
                <Row gutter={16}>
                  <Col span={12}>
                    <Statistic title="Latitude" value={currentLocation.latitude} precision={6} />
                  </Col>
                  <Col span={12}>
                    <Statistic title="Longitude" value={currentLocation.longitude} precision={6} />
                  </Col>
                </Row>
                <div style={{ marginTop: 16 }}>
                  <Text strong>Address:</Text>
                  <div style={{ marginTop: 4 }}>{currentLocation.address}</div>
                </div>
                <div style={{ marginTop: 16 }}>
                  <Tag icon={<CheckCircleOutlined />} color="success">
                    Location Sharing Active
                  </Tag>
                </div>
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <EnvironmentOutlined style={{ fontSize: 48, color: '#999' }} />
                <div style={{ marginTop: 16 }}>
                  <Text type="secondary">
                    Click "Update Location Now" to get your current position
                  </Text>
                </div>
              </div>
            )}
          </Card>
        </Col>

        {/* Location Settings Card */}
        <Col xs={24} lg={12}>
          <Card
            title={
              <>
                <SettingOutlined /> Location Settings
              </>
            }
            extra={
              <Switch
                checked={isLocationEnabled}
                onChange={handleToggleSharing}
                checkedChildren="ON"
                unCheckedChildren="OFF"
              />
            }
          >
            <List
              size="small"
              dataSource={[
                {
                  key: '1',
                  label: 'Location Sharing',
                  value: isLocationEnabled ? 'Enabled' : 'Disabled',
                },
                {
                  key: '2',
                  label: 'Service Areas',
                  value: permissions?.location_enabled ? 'Configured' : 'Not Set',
                },
                {
                  key: '3',
                  label: 'Last Update',
                  value: currentLocation ? dayjs().format('HH:mm:ss') : 'Never',
                },
              ]}
              renderItem={(item) => (
                <List.Item>
                  <Text>{item.label}</Text>
                  <Tag
                    color={
                      item.value === 'Enabled' || item.value === 'Configured' ? 'green' : 'default'
                    }
                  >
                    {item.value}
                  </Tag>
                </List.Item>
              )}
            />
          </Card>
        </Col>

        {/* Manual Location Entry */}
        <Col xs={24}>
          <Card title="Manual Location Entry">
            <Form layout="inline" onFinish={handleManualLocation}>
              <Form.Item name="latitude" rules={[{ required: true, message: 'Enter latitude' }]}>
                <Input placeholder="Latitude (e.g., 27.7172)" style={{ width: 150 }} />
              </Form.Item>
              <Form.Item name="longitude" rules={[{ required: true, message: 'Enter longitude' }]}>
                <Input placeholder="Longitude (e.g., 85.3240)" style={{ width: 150 }} />
              </Form.Item>
              <Form.Item name="address">
                <Input placeholder="Address (optional)" style={{ width: 200 }} />
              </Form.Item>
              <Form.Item>
                <Button type="primary" htmlType="submit" loading={updateLocationMutation.isLoading}>
                  Set Location
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </Col>

        {/* Location Info */}
        <Col xs={24}>
          <Card>
            <Row gutter={16}>
              <Col xs={24} sm={8}>
                <Statistic
                  title="Auto-Update Interval"
                  value="30"
                  suffix="seconds"
                  prefix={<ClockCircleOutlined />}
                />
              </Col>
              <Col xs={24} sm={8}>
                <Statistic
                  title="Tracking Status"
                  value={isLocationEnabled ? 'Active' : 'Inactive'}
                  valueStyle={{ color: isLocationEnabled ? '#10b981' : '#ff4d4f' }}
                  prefix={<PushpinOutlined />}
                />
              </Col>
              <Col xs={24} sm={8}>
                <Statistic title="GPS Accuracy" value="High" prefix={<CarOutlined />} />
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default LocationTracking;
